import {
    Alert, Button, Form, Input, Row, Space, Typography,
} from 'antd';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { startVerify2FA, useAuthStore } from '../../store/auth.store';

const { Title, Text } = Typography;
const CODE_LENGTH = 6;

interface ITwoFactorVerifyProps {
	onBack: () => void;
}

const TwoFactorVerify: FC<ITwoFactorVerifyProps> = ({ onBack }) => {
    const { t } = useTranslation('login');
    const { loading, error } = useAuthStore(
        useShallow((state) => ({ loading: state.loading, error: state.error })),
    );

    const [useBackupCode, setUseBackupCode] = useState(false);
    const [code, setCode] = useState('');

    const handleCodeChange = useCallback((value: string) => {
        const cleaned = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
        setCode(cleaned);
        if (cleaned.length === CODE_LENGTH) {
            startVerify2FA(cleaned, false);
        }
    }, []);

    const handleBackupCodeSubmit = useCallback(async (values: { backupCode: string }) => {
        await startVerify2FA(values.backupCode.trim(), true);
    }, []);

    const handleToggleBackupCode = useCallback(() => {
        setUseBackupCode((prev) => !prev);
        setCode('');
        useAuthStore.setState({ error: null });
    }, []);

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Title level={2} style={{ margin: 0, color: 'var(--color-primary)', textAlign: 'center' }}>
                {t('TWO_FACTOR.TITLE')}
            </Title>

            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                {useBackupCode ? t('TWO_FACTOR.BACKUP_DESCRIPTION') : t('TWO_FACTOR.DESCRIPTION')}
            </Text>

            {error && <Alert closable={false} message={error} type="error" showIcon />}

            {!useBackupCode ? (
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
                }}
                >
                    <Input
                        size="large"
                        value={code}
                        onChange={(e) => handleCodeChange(e.target.value)}
                        placeholder="000000"
                        maxLength={CODE_LENGTH}
                        disabled={loading}
                        autoFocus
                        style={{
                            textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 600, maxWidth: 240,
                        }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('TWO_FACTOR.CODE_HINT')}</Text>
                </div>
            ) : (
                <Form onFinish={handleBackupCodeSubmit} layout="vertical">
                    <Form.Item name="backupCode" rules={[{ required: true, message: t('TWO_FACTOR.BACKUP_REQUIRED') }]}>
                        <Input size="large" placeholder={t('TWO_FACTOR.BACKUP_PLACEHOLDER')} disabled={loading} autoFocus />
                    </Form.Item>
                    <Button type="primary" block htmlType="submit" loading={loading} size="large">
                        {t('TWO_FACTOR.VERIFY')}
                    </Button>
                </Form>
            )}

            <div style={{ textAlign: 'center' }}>
                <Space size={4}>
                    <Text type="secondary">
                        {useBackupCode ? t('TWO_FACTOR.PROBLEMS_CODE') : t('TWO_FACTOR.PROBLEMS_AUTH')}
                    </Text>
                    <Button type="link" style={{ padding: 0 }} onClick={handleToggleBackupCode} disabled={loading}>
                        {useBackupCode ? t('TWO_FACTOR.USE_CODE') : t('TWO_FACTOR.USE_BACKUP')}
                    </Button>
                </Space>
            </div>

            <div style={{ textAlign: 'center' }}>
                <Button type="link" onClick={onBack} disabled={loading}>
                    {t('TWO_FACTOR.BACK_TO_LOGIN')}
                </Button>
            </div>
        </Space>
    );
};

export default TwoFactorVerify;
